import raw from "@/data/postcodes.json";
import { MOSAIC_GROUPS, SIGNAL_KEYS, RETIREMENT_STAGES, BEDROOMS } from "@/data/mosaic";

/*
 * Filter logic (explicit):
 *  - Within Retirement stage: ANY of the selected stages (OR).
 *  - Within Property size:    ANY of the selected bedroom counts (OR).
 *  - Within Opportunity signals: ANY (OR) by default, or ALL (AND) when opportunityMatch === "all".
 *  - Across those three dimensions: ALL must match (AND).
 * Totals are always the sum of the UNIQUE matching Mosaic groups, so overlapping signals are never double-counted.
 */

const COLUMNS = raw.columns;
const DISTRICTS = raw.districts;

const naturalCompare = (a, b) => a.localeCompare(b, "en", { numeric: true });

export const DISTRICT_CODES = Object.keys(DISTRICTS).sort(naturalCompare);
export const DISTRICT_COUNT = DISTRICT_CODES.length;

export const EMPTY_FILTERS = Object.freeze({
  retirement: [],
  property: [],
  downsize: null,
  capitalRelease: null,
  sheltered: null,
  care: null,
  opportunityMatch: "any",
});

const sum = (arr) => arr.reduce((a, b) => a + b, 0);

const NATIONAL_BY_GROUP = Object.fromEntries(
  COLUMNS.map((code, i) => [code, sum(DISTRICT_CODES.map((d) => DISTRICTS[d][i] || 0))])
);

const DISTRICT_SILVER_TOTAL = Object.fromEntries(DISTRICT_CODES.map((d) => [d, sum(DISTRICTS[d])]));

export const hasDistrict = (code) => Object.prototype.hasOwnProperty.call(DISTRICTS, code);

export const silverTotalFor = (code) => DISTRICT_SILVER_TOTAL[code] ?? 0;

export const districtGroupCount = (district, groupCode) => {
  const i = COLUMNS.indexOf(groupCode);
  return hasDistrict(district) && i >= 0 ? DISTRICTS[district][i] || 0 : 0;
};

// "rg40 1ab" -> "RG40", " rg41 " -> "RG41"
export function normalisePostcode(input) {
  const s = String(input || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const full = s.match(/^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/);
  return full ? full[1] : s;
}

export function parsePostcodeList(text) {
  const collapsed = String(text || "")
    .toUpperCase()
    .replace(/\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/g, "$1");
  const tokens = collapsed.split(/[\s,;|/]+/).map(normalisePostcode).filter(Boolean);
  return [...new Set(tokens)];
}

export function searchDistricts(query, { exclude = [], limit = 7 } = {}) {
  const q = String(query || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!q) return [];
  const excluded = new Set(exclude);
  const out = [];
  for (const code of DISTRICT_CODES) {
    if (code.startsWith(q) && !excluded.has(code)) {
      out.push(code);
      if (out.length >= limit) break;
    }
  }
  return out;
}

export function getPostcodeData(postcodes) {
  const districts = [];
  const missing = [];
  for (const rawCode of postcodes) {
    const code = normalisePostcode(rawCode);
    if (!hasDistrict(code)) {
      missing.push(code);
      continue;
    }
    const row = DISTRICTS[code];
    const counts = Object.fromEntries(COLUMNS.map((c, i) => [c, row[i] || 0]));
    districts.push({ code, counts, silverTotal: DISTRICT_SILVER_TOTAL[code] });
  }
  return { districts, missing, groups: MOSAIC_GROUPS, filters: EMPTY_FILTERS };
}

export function groupMatchesFilters(group, filters = EMPTY_FILTERS) {
  if (filters.retirement?.length && !filters.retirement.includes(group.retirement)) return false;
  if (filters.property?.length && !filters.property.includes(group.bedrooms)) return false;
  const wanted = SIGNAL_KEYS.filter((k) => filters[k] === true);
  if (wanted.length) {
    const hits = wanted.filter((k) => group[k]).length;
    const ok = filters.opportunityMatch === "all" ? hits === wanted.length : hits > 0;
    if (!ok) return false;
  }
  return true;
}

export function applyAudienceFilters(data, filters) {
  const groups = data.groups.filter((g) => groupMatchesFilters(g, filters));
  const keep = new Set(groups.map((g) => g.code));
  const districts = data.districts.map((d) => ({
    ...d,
    counts: Object.fromEntries(Object.entries(d.counts).filter(([c]) => keep.has(c))),
  }));
  return { ...data, groups, districts, filters };
}

export function calculateAudienceTotal(data) {
  return sum(data.districts.map((d) => sum(Object.values(d.counts))));
}

export function calculateMosaicBreakdown(data) {
  const total = calculateAudienceTotal(data);
  return data.groups
    .map((g) => {
      const byDistrict = data.districts.map((d) => ({ code: d.code, households: d.counts[g.code] || 0 }));
      const households = sum(byDistrict.map((b) => b.households));
      return { ...g, households, share: total ? households / total : 0, byDistrict };
    })
    .sort((a, b) => b.households - a.households);
}

export function calculateAttributeBreakdown(data) {
  const rows = calculateMosaicBreakdown(data);
  const total = sum(rows.map((r) => r.households));
  const by = (pred) => sum(rows.filter(pred).map((r) => r.households));
  return {
    total,
    retirement: Object.fromEntries(RETIREMENT_STAGES.map((s) => [s.key, by((r) => r.retirement === s.key)])),
    property: Object.fromEntries(BEDROOMS.map((b) => [b, by((r) => r.bedrooms === b)])),
    signals: Object.fromEntries(SIGNAL_KEYS.map((k) => [k, by((r) => r[k])])),
  };
}

export function calculateGeographicBreakdown(data) {
  const total = calculateAudienceTotal(data);
  return data.districts.map((d) => {
    const households = sum(Object.values(d.counts));
    return { code: d.code, households, silverTotal: d.silverTotal, share: total ? households / total : 0 };
  });
}

// Average matching households per district across the whole dataset, for the same filters.
export function calculateDatasetBenchmark(filters) {
  const national = sum(MOSAIC_GROUPS.filter((g) => groupMatchesFilters(g, filters)).map((g) => NATIONAL_BY_GROUP[g.code]));
  return { perDistrict: national / DISTRICT_COUNT, districtCount: DISTRICT_COUNT };
}

export function countForFilters(postcodes, filters) {
  return calculateAudienceTotal(applyAudienceFilters(getPostcodeData(postcodes), filters));
}

export function buildAudience(postcodes, filters) {
  const data = getPostcodeData(postcodes);
  const filtered = applyAudienceFilters(data, filters);
  const total = calculateAudienceTotal(filtered);
  const silverTotal = calculateAudienceTotal(data);
  const benchmark = calculateDatasetBenchmark(filters);
  const perDistrict = data.districts.length ? total / data.districts.length : 0;
  return {
    areas: data.districts.map((d) => d.code),
    total,
    silverTotal,
    matchedProfiles: filtered.groups.length,
    profileCount: MOSAIC_GROUPS.length,
    attributes: calculateAttributeBreakdown(filtered),
    geography: calculateGeographicBreakdown(filtered),
    mosaic: calculateMosaicBreakdown(filtered),
    benchmark: { ...benchmark, selectedPerDistrict: perDistrict, ratio: benchmark.perDistrict ? perDistrict / benchmark.perDistrict : 0 },
  };
}
